// components/SidsSnapshotModule.js - Current vs Previous SIDs summary
import React, { useEffect, useState } from 'react';
import { RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import { dashboardStyles } from '../dashboardStyles';
import { fetchSidSnapshot } from '../services/api/analysisApi';
import { triggerIngestion } from '../services/api/ingestionApi';

const SidsSnapshotModule = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [meta, setMeta] = useState(null);

    const fetchSnapshot = async (refresh = false) => {
        setLoading(true);
        setError('');

        try {
            if (refresh) {
                await triggerIngestion('frontend-sids-snapshot');
            }
            const result = await fetchSidSnapshot(refresh);

            setMeta(result.meta || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSnapshot(false);
    }, []);

    const formatTimeAgo = (date) => {
        if (!date) return 'Never';
        const now = new Date();
        const diffMs = now - new Date(date);
        const diffMins = Math.round(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;

        const diffHours = Math.round(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;

        const diffDays = Math.round(diffHours / 24);
        return `${diffDays}d ago`;
    };

    const currentCount = meta?.totalCurrentSids || 0;
    const previousCount = meta?.totalPreviousSids || 0;
    const updatedAt = meta?.currentSnapshotTime || null;

    return (
        <div style={dashboardStyles.sidsSnapshotModule}>
            <div style={dashboardStyles.originsHeader}>
                <div style={{ ...dashboardStyles.originsIcon, background: '#22c55e' }}>🧾</div>
                <div>
                    <div style={dashboardStyles.originsTitle}>
                        SID Snapshot
                        <span style={{
                            fontSize: '13px',
                            color: '#64748b',
                            marginLeft: '8px',
                            fontWeight: '400'
                        }}>
                            Current vs Previous
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: error ? '#ef4444' : loading ? '#f59e0b' : '#10b981',
                        animation: loading ? 'pulse 2s infinite' : 'none'
                    }}></div>

                    <span style={{
                        fontSize: '9px',
                        color: error ? '#ef4444' : loading ? '#f59e0b' : '#10b981',
                        fontWeight: '500'
                    }}>
                        {error ? 'Error' : loading ? 'Loading...' : 'Live Data'}
                    </span>

                    <button
                        onClick={() => fetchSnapshot(true)}
                        disabled={loading}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#22c55e',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            padding: '2px',
                            borderRadius: '3px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            opacity: loading ? 0.5 : 1,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = 'rgba(34, 197, 94, 0.1)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                        title="Refresh snapshot"
                    >
                        <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                    </button>
                </div>
            </div>

            {updatedAt && (
                <div style={{
                    fontSize: '8px',
                    color: '#64748b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <Clock size={8} />
                    Updated {formatTimeAgo(updatedAt)}
                </div>
            )}

            {error ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    flexDirection: 'column',
                    gap: '8px',
                    color: '#ef4444'
                }}>
                    <AlertTriangle size={24} style={{ opacity: 0.5 }} />
                    <div style={{ fontSize: '11px', textAlign: 'center' }}>
                        Failed to load snapshot
                    </div>
                </div>
            ) : (
                <div style={{
                    ...dashboardStyles.originsList,
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '12px'
                }}>
                    {[
                        { label: 'Current SIDs', value: currentCount, color: '#22c55e' },
                        { label: 'Previous SIDs', value: previousCount, color: '#f59e0b' }
                    ].map((item) => (
                        <div
                            key={item.label}
                            style={{
                                ...dashboardStyles.originItem,
                                padding: '10px 12px',
                                minHeight: '56px',
                                borderColor: 'rgba(148, 163, 184, 0.2)'
                            }}
                        >
                            <span style={{
                                ...dashboardStyles.originName,
                                fontSize: '13px',
                                maxWidth: '120px'
                            }}>{item.label}</span>
                            <span style={{
                                ...dashboardStyles.originCount,
                                color: item.color,
                                fontSize: '18px'
                            }}>{item.value.toLocaleString()}</span>
                        </div>
                    ))}
                </div>
            )}

            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                `}
            </style>
        </div>
    );
};

export default SidsSnapshotModule;
